import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link, isRouteErrorResponse, useLocation, useRouteError } from 'react-router';
import { Compass, Home, LockKeyhole, RotateCw } from 'lucide-react';
import { Button, ButtonLink } from '@/components/ui/Button';
import { EmptyState, ErrorState } from '@/components/ui/feedback';
import { LogoMark } from '@/components/shell/Logo';
import { useI18n } from '@/lib/i18n';

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-canvas px-6">
      <div className="w-full max-w-lg">
        <div className="mb-2 flex justify-center">
          <LogoMark size={40} />
        </div>
        {children}
      </div>
    </div>
  );
}

/** Last-resort boundary: a rendering bug shows a calm, actionable screen instead of a white page. */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled UI error', error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <Centered>
          <ErrorState
            title="Something broke on this screen"
            description="The error has been logged. Reloading usually fixes it; if it keeps happening, tell your library administrator."
            onRetry={() => window.location.reload()}
            retrying={false}
          />
        </Centered>
      );
    }
    return this.props.children;
  }
}

/** React Router errorElement: route loading errors and thrown responses. */
export function RouteError() {
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundPage />;
  }
  return (
    <Centered>
      <ErrorState error={error} title="This page could not be shown" onRetry={() => window.location.reload()} />
    </Centered>
  );
}

/** 404. Inside a shell pass `inShell` so it renders in the content area rather than full screen. */
export function NotFoundPage({ inShell }: { inShell?: boolean }) {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const staff = pathname.startsWith('/staff');
  const body = (
    <EmptyState
      icon={Compass}
      size="lg"
      title={t('error.notFoundTitle')}
      description={t('error.notFoundBody')}
      action={
        <ButtonLink to={staff ? '/staff' : '/'} leftIcon={<Home className="h-4 w-4" aria-hidden="true" />}>
          {t('error.goHome')}
        </ButtonLink>
      }
      secondaryAction={
        <Button variant="secondary" onClick={() => window.history.back()}>
          {t('common.back')}
        </Button>
      }
    />
  );
  return inShell ? body : <Centered>{body}</Centered>;
}

/** 403: signed in, but missing the permission for this screen. */
export function ForbiddenPage({ inShell = true }: { inShell?: boolean }) {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const body = (
    <EmptyState
      icon={LockKeyhole}
      tone="warning"
      size="lg"
      title={t('error.forbiddenTitle')}
      description={t('error.forbiddenBody')}
      action={
        <ButtonLink to={pathname.startsWith('/staff') ? '/staff' : '/'} variant="secondary" leftIcon={<RotateCw className="h-4 w-4" aria-hidden="true" />}>
          {t('error.goHome')}
        </ButtonLink>
      }
      secondaryAction={
        <Link to="/staff/login" className="text-sm font-medium text-sapphire-700 hover:underline dark:text-sapphire-300">
          {t('nav.signIn')}
        </Link>
      }
    />
  );
  return inShell ? body : <Centered>{body}</Centered>;
}
